import { Link } from "@tanstack/react-router";
import type { Post } from "@/data/posts";
import { sizedImage } from "@/lib/image";
import { formatDate } from "@/lib/format";
import { categorySlug } from "@/config";

interface Props {
  post: Post;
  priority?: boolean;
}

export function PostCard({ post, priority = false }: Props) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-shadow hover:shadow-md">
      <Link
        to="/post/$slug"
        params={{ slug: post.slug }}
        className="block aspect-[4/3] overflow-hidden bg-muted"
      >
        <img
          src={sizedImage(post.thumbnail, 800)}
          alt={post.title}
          width={800}
          height={600}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <Link
          to="/category/$category"
          params={{ category: categorySlug(post.category) }}
          className="inline-block w-fit rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground"
        >
          {post.category}
        </Link>
        <h3 className="text-lg font-semibold leading-snug">
          <Link to="/post/$slug" params={{ slug: post.slug }}>
            {post.title}
          </Link>
        </h3>
        <p className="line-clamp-2 text-sm text-muted-foreground">
          {post.description}
        </p>
        <div className="mt-auto flex items-center gap-2 pt-2 text-xs text-muted-foreground">
          <img
            src="/admin-avatar.webp"
            alt=""
            width={20}
            height={20}
            loading="lazy"
            decoding="async"
            className="h-5 w-5 rounded-full object-cover"
          />
          <span>{post.author}</span>
          <span aria-hidden="true">·</span>
          <time dateTime={post.publishDate}>{formatDate(post.publishDate)}</time>
        </div>
      </div>
    </article>
  );
}
